import React, { useEffect, useMemo, useRef, useState } from 'react';
import FsLightbox from 'fslightbox-react';
import { getTicketImages } from '../../utils/ticketImages';
import { resolveTicketPhotoUrl } from '../../utils/ticketPhotoUrl';
import { firebase_app } from '../../Config/Config';
import './ticketPhotoGallery.scss';

const requiresFirebaseAuth = reference => {
  const value = typeof reference === 'string' ? reference.trim() : '';
  return /^gs:\/\//i.test(value) || /^clients\/[^/]+\/tickets\/[^/]+\/photos\/[^/]+$/i.test(value);
};

const revokeObjectUrls = urls => {
  if (typeof URL === 'undefined' || typeof URL.revokeObjectURL !== 'function') {
    return;
  }

  urls.forEach(url => URL.revokeObjectURL(url));
};

const materializePhoto = resolvedPhoto => {
  if (resolvedPhoto && resolvedPhoto.kind === 'blob') {
    if (typeof URL === 'undefined' || typeof URL.createObjectURL !== 'function') {
      throw new Error('Blob URLs are not supported');
    }

    return {
      url: URL.createObjectURL(resolvedPhoto.blob),
      owned: true,
    };
  }

  if (resolvedPhoto && resolvedPhoto.kind === 'borrowed' && resolvedPhoto.url) {
    return { url: resolvedPhoto.url, owned: false };
  }

  throw new Error('Photo resolution returned no usable source');
};

const TicketPhotoGallery = ({ ticket, images, className = '', imageClassName = '', label = 'Photo de l’avis', auth }) => {
  const ticketImages = useMemo(() => images || getTicketImages(ticket), [images, ticket]);
  const protectedPhotos = useMemo(() => ticketImages.some(requiresFirebaseAuth), [ticketImages]);
  const photoAuth = useMemo(
    () => (protectedPhotos ? (auth || firebase_app.auth()) : auth),
    [auth, protectedPhotos]
  );
  const [authUser, setAuthUser] = useState(photoAuth ? photoAuth.currentUser : null);
  const [resolvedImages, setResolvedImages] = useState([]);
  const [resolutionStatus, setResolutionStatus] = useState(ticketImages.length ? 'loading' : 'empty');
  const [resolutionAttempt, setResolutionAttempt] = useState(0);
  const retryRequestedRef = useRef(false);
  const ownedObjectUrlsRef = useRef([]);
  const [lightbox, setLightbox] = useState({ toggler: false, slide: 1 });

  useEffect(() => {
    if (!photoAuth || !protectedPhotos) {
      return undefined;
    }

    setAuthUser(photoAuth.currentUser);
    return photoAuth.onAuthStateChanged(setAuthUser);
  }, [photoAuth, protectedPhotos]);

  useEffect(() => () => {
    const ownedUrls = ownedObjectUrlsRef.current;
    ownedObjectUrlsRef.current = [];
    revokeObjectUrls(ownedUrls);
  }, []);

  useEffect(() => {
    let active = true;
    const preserveResolvedImages = retryRequestedRef.current;
    retryRequestedRef.current = false;

    if (!ticketImages.length) {
      const ownedUrls = ownedObjectUrlsRef.current;
      ownedObjectUrlsRef.current = [];
      revokeObjectUrls(ownedUrls);
      setResolvedImages([]);
      setResolutionStatus('empty');
      return () => {
        active = false;
      };
    }

    if (!preserveResolvedImages) {
      const ownedUrls = ownedObjectUrlsRef.current;
      ownedObjectUrlsRef.current = [];
      revokeObjectUrls(ownedUrls);
      setResolvedImages([]);
      setResolutionStatus('loading');
    }

    Promise.allSettled(
      ticketImages.map(reference => resolveTicketPhotoUrl(reference))
    ).then(results => {
      const materializedPhotos = [];

      results.forEach(result => {
        if (result.status !== 'fulfilled') {
          return;
        }

        try {
          materializedPhotos.push(materializePhoto(result.value));
        } catch (error) {
          // Treat client-side Blob materialization failures like Storage failures.
        }
      });

      const urls = materializedPhotos.map(photo => photo.url);
      const ownedUrls = materializedPhotos
        .filter(photo => photo.owned)
        .map(photo => photo.url);

      if (!active) {
        revokeObjectUrls(ownedUrls);
        return;
      }

      const previousOwnedUrls = ownedObjectUrlsRef.current;
      ownedObjectUrlsRef.current = ownedUrls;
      setResolvedImages(urls);
      setResolutionStatus(
        urls.length === ticketImages.length ? 'ready' : (urls.length ? 'partial' : 'error')
      );
      revokeObjectUrls(previousOwnedUrls);
    });

    return () => {
      active = false;
    };
  }, [authUser, photoAuth, resolutionAttempt, ticketImages]);

  const retryPhotos = () => {
    retryRequestedRef.current = true;
    setResolutionStatus(resolvedImages.length ? 'retrying' : 'loading');
    setResolutionAttempt(current => current + 1);
  };

  if (!ticketImages.length) {
    return null;
  }

  if (resolutionStatus === 'loading') {
    return <p className="ticket-photo-gallery__status" role="status">Chargement des photos…</p>;
  }

  if (resolutionStatus === 'error') {
    return (
      <div className={`ticket-photo-gallery ${className}`.trim()}>
        <p className="ticket-photo-gallery__status" role="status">Photos indisponibles</p>
        <button type="button" className="btn btn-outline-primary" onClick={retryPhotos}>
          Réessayer les photos
        </button>
      </div>
    );
  }

  return (
    <div className={`ticket-photo-gallery ${className}`.trim()}>
      {resolutionStatus === 'partial' && (
        <div className="mb-3">
          <p className="ticket-photo-gallery__status" role="status">
            Photos partiellement chargées : {resolvedImages.length} sur {ticketImages.length}.
          </p>
          <button type="button" className="btn btn-outline-primary" onClick={retryPhotos}>
            Réessayer les photos
          </button>
        </div>
      )}
      {resolutionStatus === 'retrying' && (
        <p className="ticket-photo-gallery__status" role="status">
          Nouvelle tentative de chargement des photos…
        </p>
      )}
      <FsLightbox
        toggler={lightbox.toggler}
        sources={resolvedImages}
        type="image"
        slide={lightbox.slide}
      />
      <div className="row g-2">
        {resolvedImages.map((src, index) => (
          <div className={resolvedImages.length === 1 ? 'col-12' : 'col-4'} key={src}>
            <button
              type="button"
              className="ticket-photo-gallery__button"
              onClick={() => setLightbox(current => ({ toggler: !current.toggler, slide: index + 1 }))}
              aria-label={`${label} ${index + 1} sur ${resolvedImages.length}`}
            >
              <img
                src={src}
                alt={`${label} ${index + 1}`}
                className={`img-thumbnail ticket-photo-gallery__image ${imageClassName}`.trim()}
                loading="lazy"
                decoding="async"
              />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

export default TicketPhotoGallery;
